import React, { useState, useEffect, useRef } from "react";
import {
	getCurrentUser,
	sendUserAttributeVerificationCode,
	confirmUserAttribute,
	resendSignUpCode,
	confirmSignIn,
	confirmSignUp,
} from "aws-amplify/auth";
import LoadingSpinner from "../../global/LoadingSpinner";
import { formatMessage } from "../../../js/formUtils";

/**
 * VerifyUserAttribute Component
 *
 * Props:
 * @param {function} onVerificationSuccess - Callback function to be called when verification is successful
 * @param {string} email - The email address of the user
 * @param {string} phoneNumber - The phone number of the user
 * @param {boolean} isPhoneVerified - If true, the user has verified their phone number
 * @param {boolean} isEmailVerified - If true, the user has verified their email address
 * @param {string} organizationCreationStatusUrl - Status URL for organization creation
 * @param {function} onCancel - Callback function to be called when the user cancels the verification process
 */

function VerifyAccount(props) {
	const codeLength = 6;
	const [codeDigits, setCodeDigits] = useState(Array(codeLength).fill(""));
	const inputRefs = useRef([]);
	const firstInputRef = useRef(null);

	// State for the complete confirmation code
	const [confirmationCode, setConfirmationCode] = useState("");
	const [confirmationError, setConfirmationError] = useState("");
	const [confirmationSuccess, setConfirmationSuccess] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isPollingOrganization, setIsPollingOrganization] = useState(false);
	const [verificationMethod, setVerificationMethod] = useState(null);
	const isMountedRef = useRef(true);
	const isBusy = isSubmitting || isPollingOrganization;

	const clearState = () => {
		setConfirmationCode("");
		setConfirmationError("");
		setConfirmationSuccess(false);
		setIsSubmitting(false);
		setCodeDigits(Array(codeLength).fill(""));

		// Clear the actual input field values
		inputRefs.current.forEach((input) => {
			if (input) {
				input.value = "";
			}
		});
	};

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Handle prop changes and reset component state
	useEffect(() => {
		// Clear previous state when verification status changes
		clearState();

		if (props.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
			setVerificationMethod("phone");
		} else if (
			props.isEmailVerified === false ||
			props.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
		) {
			setVerificationMethod("email");
		} else if (props.isPhoneVerified === false) {
			setVerificationMethod("phone");
		}
	}, [props.isPhoneVerified, props.isEmailVerified, props.signInStep]);

	/**
	 * Resend verification code for user signup confirmation
	 * @returns {Promise} Result of resending verification code
	 */
	const resendVerificationCode = async () => {
		clearState();
		let verificationCodeResponse = null;
		try {
			await getCurrentUser();
			verificationCodeResponse = await sendUserAttributeVerificationCode({
				userAttributeKey:
					verificationMethod === "phone" ? "phone_number" : verificationMethod,
			});
		} catch (authError) {
			await resendSignUpCode({
				username: props.email,
			});
		}

		if (
			verificationCodeResponse &&
			verificationCodeResponse.CodeDeliveryDetails
		) {
			let verificationMethod = null;
			if (
				verificationCodeResponse.CodeDeliveryDetails.DeliveryMedium === "EMAIL"
			) {
				verificationMethod = "email";
			} else if (
				verificationCodeResponse.CodeDeliveryDetails.DeliveryMedium === "SMS"
			) {
				verificationMethod = "phone";
			}
			if (verificationCodeResponse.CodeDeliveryDetails.Destination) {
				verificationMethod = `${verificationMethod} (${verificationCodeResponse.CodeDeliveryDetails.Destination})`;
			}
			setVerificationMethod(verificationMethod);
		}

		return verificationCodeResponse;
	};

	// Use the password from props for auto-login if provided

	const confirmUserAccount = async (e) => {
		if (e) {
			e.preventDefault();
		}
		setConfirmationError("");
		setIsSubmitting(true);

		if (!confirmationCode || confirmationCode.length !== 6) {
			setConfirmationError("Please enter a valid 6-digit verification code");
			setIsSubmitting(false);
			return;
		}

		try {
			// Branch based on the signInStep passed from Login (Amplify v6)
			if (
				props.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
				props.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
			) {
				// Mid sign-in MFA/email challenge
				await confirmSignIn({
					challengeResponse: confirmationCode,
				});
			} else if (props.signInStep === "CONFIRM_SIGN_UP") {
				// New account confirmation
				await confirmSignUp({
					username: props.email,
					confirmationCode: confirmationCode,
				});
			} else {
				// Already authenticated - verifying an attribute (email/phone)
				try {
					await currentAuthenticatedUser();
					await confirmUserAttribute({
						userAttributeKey:
							verificationMethod === "phone" ? "phone_number" : "email",
						confirmationCode: confirmationCode,
					});
				} catch (authError) {
					// Fallback: not authenticated and no explicit step - try sign-up confirmation
					await confirmSignUp({
						username: props.email,
						confirmationCode: confirmationCode,
					});
				}
			}
			setConfirmationSuccess(true);
			setConfirmationError("");
			setIsSubmitting(false);

			if (props.organizationCreationStatusUrl) {
				setIsPollingOrganization(true);
			}

			props.onVerificationSuccess();
		} catch (err) {
			// If user is already confirmed, trigger login flow instead of showing error
			if (
				err.code === "UserAlreadyConfirmedException" ||
				(err.message &&
					err.message.toLowerCase().includes("status is confirmed"))
			) {
				setIsSubmitting(false);
				props.onVerificationSuccess();
				return;
			}
			setConfirmationError(
				err.message || "Failed to confirm your account. Please try again.",
			);
			setIsSubmitting(false);
			setIsPollingOrganization(false);
		}
	};

	// Focus first input on mount
	useEffect(() => {
		if (firstInputRef.current) {
			// Use a shorter timeout to improve perceived performance
			setTimeout(() => {
				firstInputRef.current.focus();
			}, 50);
		}
	}, []);

	const handleCodeInput = (e, index) => {
		const target = e.target;
		const value = target.value;

		// Only allow numeric input
		if (!/^\d*$/.test(value)) {
			return;
		}

		// Handle case when user types multiple digits
		if (value.length > 1) {
			const digits = value.split("");

			// Update current and subsequent inputs with the digits
			const newCodeDigits = [...codeDigits];

			for (let i = 0; i < digits.length && index + i < codeLength; i++) {
				newCodeDigits[index + i] = digits[i];
			}

			setCodeDigits(newCodeDigits);

			// Focus on the next empty input or the last input
			const nextEmptyIndex = newCodeDigits.findIndex((digit) => digit === "");
			if (nextEmptyIndex !== -1 && nextEmptyIndex < codeLength) {
				inputRefs.current[nextEmptyIndex].focus();
			} else {
				inputRefs.current[codeLength - 1].focus();
			}
		} else {
			// Handle single digit input
			const newCodeDigits = [...codeDigits];
			newCodeDigits[index] = value;
			setCodeDigits(newCodeDigits);

			// Move focus to next input if available
			if (value && index < codeLength - 1) {
				inputRefs.current[index + 1].focus();
			}

			// Notify parent of change
			const code = newCodeDigits.join("");
			setConfirmationCode(code);

			// Check if code is complete
			if (newCodeDigits.every((digit) => digit !== "") && props.onComplete) {
				props.onComplete(code);
			}
		}
	};

	const handleKeyDown = (e, index) => {
		// Handle backspace
		if (e.key === "Backspace") {
			const newCodeDigits = [...codeDigits];

			// If current input is empty and not the first one, move to previous input
			if (newCodeDigits[index] === "" && index > 0) {
				newCodeDigits[index - 1] = "";
				setCodeDigits(newCodeDigits);
				inputRefs.current[index - 1].focus();
			} else {
				// Clear current input
				newCodeDigits[index] = "";
				setCodeDigits(newCodeDigits);
			}

			// Notify parent of change
			setConfirmationCode(newCodeDigits.join(""));
			e.preventDefault();
		}

		// Handle arrow keys
		if (e.key === "ArrowLeft" && index > 0) {
			inputRefs.current[index - 1].focus();
			e.preventDefault();
		}

		if (e.key === "ArrowRight" && index < codeLength - 1) {
			inputRefs.current[index + 1].focus();
			e.preventDefault();
		}
	};

	const handlePaste = (e) => {
		e.preventDefault();

		const pastedData =
			(e.clipboardData && e.clipboardData.getData("text")) || "";
		const digits = pastedData.replace(/\D/g, "").split("").slice(0, codeLength);

		if (digits.length > 0) {
			const newCodeDigits = [...codeDigits];

			digits.forEach((digit, i) => {
				if (i < codeLength) {
					newCodeDigits[i] = digit;
				}
			});

			setCodeDigits(newCodeDigits);

			// Focus on the next empty input or the last filled input
			const nextEmptyIndex = newCodeDigits.findIndex((digit) => digit === "");
			if (nextEmptyIndex !== -1) {
				inputRefs.current[nextEmptyIndex].focus();
			} else {
				inputRefs.current[Math.min(digits.length, codeLength) - 1].focus();
			}

			// Notify parent of change
			const code = newCodeDigits.join("");
			setConfirmationCode(code);
		}
	};

	return (
		<div className="confirmation-container">
			<h3>
				{props.signInStep?.startsWith("CONFIRM_SIGN_IN")
					? "Confirm Sign In"
					: "Verify Your Account"}
			</h3>
			<p>
				{verificationMethod
					? `A verification code has been sent to your ${verificationMethod}. Please enter it below to confirm your account.`
					: `You should receive a verification code at ${
							props.email || "your email or phone number"
						} within a few minutes. Please enter it below to confirm your account.`}
			</p>

			{confirmationError && (
				<div className="error-message">{confirmationError}</div>
			)}

			{confirmationSuccess && (
				<div className="success-message">
					{isPollingOrganization ? (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "10px",
							}}>
							<span>
								Your account has been confirmed. Finalizing organization
								details. You will get redirected when this is complete...
							</span>
							<LoadingSpinner size="20px" inline />
						</div>
					) : (
						"Your account has been verified! Redirecting you now..."
					)}
				</div>
			)}

			<form onSubmit={confirmUserAccount} className="verification-form">
				<div className="confirmation-form">
					<div className="confirmation-input-group">
						<label>Verification Code</label>
						<div className="verification-code-container">
							{codeDigits.map((digit, index) => (
								<input
									key={index}
									type="text"
									inputMode="numeric"
									pattern="[0-9]*"
									maxLength="1"
									value={digit}
									onChange={(e) => handleCodeInput(e, index)}
									onInput={(e) => handleCodeInput(e, index)}
									onKeyDown={(e) => handleKeyDown(e, index)}
									onFocus={(e) => e.currentTarget.select()}
									onPaste={(e) => handlePaste(e)}
									ref={(el) => {
										if (el) {
											inputRefs.current[index] = el;
											if (index === 0) firstInputRef.current = el;
										}
									}}
									disabled={isBusy}
									className="code-input"
									aria-label={`Digit ${index + 1}`}
									autoComplete="off"
								/>
							))}
						</div>
					</div>
					<div className="verify-actions">
						<button
							type="submit"
							className="submit-button"
							disabled={!codeDigits || codeDigits.length !== 6 || isBusy}>
							{isPollingOrganization
								? "Finalizing Organization..."
								: isSubmitting
									? "Verifying..."
									: "Verify"}
						</button>
					</div>
				</div>
			</form>
			<div className="form-links">
				Didn't receive a code?{" "}
				<a
					href="#"
					className={`form-text-link ${isBusy ? "disabled" : ""}`}
					onClick={(e) => {
						e.preventDefault();
						if (!isBusy) {
							resendVerificationCode();
						}
					}}>
					Resend Verification Code
				</a>
			</div>
			<div className="form-links">
				<a
					href="#"
					className={`form-text-link ${isBusy ? "disabled" : ""}`}
					onClick={(e) => {
						e.preventDefault();
						if (!isBusy) {
							props.onCancel();
						}
					}}>
					Return to Login/Register
				</a>
			</div>
		</div>
	);
}

export default VerifyAccount;
